import { useCallback, useEffect, useState } from 'react'
import type { AdLayoutType, AdSlot, AdSlotContext, UseAdSlotResult, UseAdsResult } from './ads.types'
import { AD_SLOTS, filterItemsByCategory, filterItemsByKeyword, mockAdsData } from './mockAds'

// ==============================================
// Configuration - Change only this section when
// switching from mock data to real API
// ==============================================

const AD_CONFIG = {
  // Set to true to use real API, false for mock data
  USE_REAL_API: false,

  // API endpoint (used when USE_REAL_API is true)
  API_BASE_URL: '/api/ads',

  // Simulated network delay in ms (used when USE_REAL_API is false)
  MOCK_DELAY: 500,

  // Enable console logging for debugging
  DEBUG: true,
}

// ==============================================
// Fetch Functions
// ==============================================

async function fetchMockAds(slotName: string, context?: AdSlotContext): Promise<AdSlot> {
  // Simulate network delay
  await new Promise((resolve) => setTimeout(resolve, AD_CONFIG.MOCK_DELAY))

  const slotData = mockAdsData[slotName]

  if (!slotData) {
    throw new Error(`Ad slot "${slotName}" not found`)
  }

  // Apply context-based filtering
  let filteredSlot = { ...slotData }

  if (slotName === AD_SLOTS.PDP_BILLBOARD && context?.categoryId) {
    filteredSlot = {
      ...slotData,
      items: filterItemsByCategory(slotData.items, context.categoryId),
    }
  } else if (slotName === AD_SLOTS.SEARCH_BANNER && context?.query) {
    filteredSlot = {
      ...slotData,
      items: filterItemsByKeyword(slotData.items, context.query),
    }
  }

  if (AD_CONFIG.DEBUG) {
    console.log(`[AdManager] Loaded mock data for slot: ${slotName}`, {
      context,
      originalItemCount: slotData.items.length,
      filteredItemCount: filteredSlot.items.length,
      items: filteredSlot.items,
    })
  }

  return filteredSlot
}

async function fetchRealAds(slotName: string, context?: AdSlotContext): Promise<AdSlot> {
  const params = new URLSearchParams()

  if (context?.categoryId) {
    params.set('category_id', context.categoryId)
  }
  if (context?.query) {
    params.set('query', context.query)
  }
  if (context?.productId) {
    params.set('product_id', context.productId)
  }

  const queryString = params.toString()
  const url = `${AD_CONFIG.API_BASE_URL}/${slotName}${queryString ? `?${queryString}` : ''}`

  const response = await fetch(url)

  if (!response.ok) {
    throw new Error(`Failed to fetch ads for slot "${slotName}": ${response.statusText}`)
  }

  const data = await response.json()

  if (AD_CONFIG.DEBUG) {
    console.log(`[AdManager] Loaded API data for slot: ${slotName}`, { context, data })
  }

  return data
}

async function fetchAds(slotName: string, context?: AdSlotContext): Promise<AdSlot> {
  if (AD_CONFIG.USE_REAL_API) {
    return fetchRealAds(slotName, context)
  }
  return fetchMockAds(slotName, context)
}

// ==============================================
// useAds Hook (Basic)
// ==============================================

/**
 * Basic hook for fetching ad data for a specific slot.
 *
 * Usage:
 * ```tsx
 * const { data, isLoading, error, refetch } = useAds('hp_hero');
 * ```
 */
export function useAds(slotName: string): UseAdsResult {
  const [data, setData] = useState<AdSlot | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)

  useEffect(() => {
    let isMounted = true

    const fetchData = async () => {
      try {
        const result = await fetchAds(slotName)

        if (isMounted) {
          setData(result)
          setError(null)
          setIsLoading(false)
        }
      } catch (err) {
        if (isMounted) {
          setError(err instanceof Error ? err : new Error('Unknown error'))
          setIsLoading(false)
        }

        if (AD_CONFIG.DEBUG) {
          console.error(`[AdManager] Error fetching slot "${slotName}":`, err)
        }
      }
    }

    fetchData()

    return () => {
      isMounted = false
    }
  }, [slotName])

  const refetch = useCallback(async () => {
    if (AD_CONFIG.DEBUG) {
      console.log(`[AdManager] Refetching slot: ${slotName}`)
    }
    setIsLoading(true)
    try {
      const result = await fetchAds(slotName)
      setData(result)
      setError(null)
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Unknown error'))
    } finally {
      setIsLoading(false)
    }
  }, [slotName])

  return { data, isLoading, error, refetch }
}

// ==============================================
// useAdSlot Hook (Context-Aware)
// ==============================================

/**
 * Context-aware hook for fetching ad data with filtering support.
 *
 * Usage:
 * ```tsx
 * // For PDP billboard - filters by category
 * const { data, layoutType } = useAdSlot('pdp_billboard', { categoryId: 'electronics' });
 *
 * // For Search banner - filters by query
 * const { data, layoutType } = useAdSlot('search_banner', { query: 'laptop deals' });
 *
 * // For Homepage slots - no context needed
 * const { data, layoutType } = useAdSlot('hp_hero');
 * ```
 */
export function useAdSlot(slotId: string, context?: AdSlotContext): UseAdSlotResult {
  const [data, setData] = useState<AdSlot | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)

  useEffect(() => {
    let isMounted = true

    const fetchData = async () => {
      try {
        const result = await fetchAds(slotId, context)

        if (isMounted) {
          setData(result)
          setError(null)
          setIsLoading(false)
        }
      } catch (err) {
        if (isMounted) {
          setError(err instanceof Error ? err : new Error('Unknown error'))
          setIsLoading(false)
        }

        if (AD_CONFIG.DEBUG) {
          console.error(`[AdManager] Error fetching slot "${slotId}":`, err)
        }
      }
    }

    fetchData()

    return () => {
      isMounted = false
    }
  }, [slotId, context])

  const refetch = useCallback(async () => {
    if (AD_CONFIG.DEBUG) {
      console.log(`[AdManager] Refetching slot: ${slotId} with context:`, context)
    }
    setIsLoading(true)
    try {
      const result = await fetchAds(slotId, context)
      setData(result)
      setError(null)
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Unknown error'))
    } finally {
      setIsLoading(false)
    }
  }, [slotId, context])

  // Extract layout type from data
  const layoutType: AdLayoutType | null = data?.layout_type ?? null

  return { data, isLoading, error, refetch, layoutType }
}

// ==============================================
// Utility Functions
// ==============================================

/**
 * Prefetch ad data for multiple slots (useful for SSR or preloading)
 */
export async function prefetchAds(slotNames: string[]): Promise<Record<string, AdSlot>> {
  const results: Record<string, AdSlot> = {}

  await Promise.all(
    slotNames.map(async (slotName) => {
      try {
        results[slotName] = await fetchAds(slotName)
      } catch (err) {
        console.error(`[AdManager] Failed to prefetch slot "${slotName}":`, err)
      }
    }),
  )

  return results
}

/**
 * Track ad impression (call when ad becomes visible)
 */
export function trackAdImpression(slotId: string, itemId: string): void {
  if (AD_CONFIG.DEBUG) {
    console.log(`[AdManager] Impression tracked: ${slotId} / ${itemId}`)
  }

  // TODO: Send impression event to analytics/backend
  // if (AD_CONFIG.USE_REAL_API) {
  //   fetch(`${AD_CONFIG.API_BASE_URL}/track/impression`, {
  //     method: 'POST',
  //     body: JSON.stringify({ slotId, itemId, timestamp: Date.now() }),
  //   });
  // }
}

/**
 * Track ad click (call when user clicks on ad)
 */
export function trackAdClick(slotId: string, itemId: string, url: string): void {
  if (AD_CONFIG.DEBUG) {
    console.log(`[AdManager] Click tracked: ${slotId} / ${itemId} -> ${url}`)
  }

  // TODO: Send click event to analytics/backend
  // if (AD_CONFIG.USE_REAL_API) {
  //   fetch(`${AD_CONFIG.API_BASE_URL}/track/click`, {
  //     method: 'POST',
  //     body: JSON.stringify({ slotId, itemId, url, timestamp: Date.now() }),
  //   });
  // }
}

export default useAds
